import type { Rule, Target } from "../rules/ruleTypes";
import { targetAppKey } from "../rules/ruleDerivation";
import { MARKETING_DEMO_DEVICE_ID, MARKETING_DEMO_RULE_ID_PREFIX } from "./constants";
import { DEFAULT_MARKETING_SEED_APPS } from "./seedApps";
import type { MarketingSeedApp } from "./types";

const DEMO_NOW = Date.UTC(2026, 5, 1);

function resolveSeedApps(seedApps: MarketingSeedApp[]): MarketingSeedApp[] {
  return seedApps.length > 0 ? seedApps : DEFAULT_MARKETING_SEED_APPS;
}

export function buildMarketingDemoTargets(seedApps: MarketingSeedApp[]): Target[] {
  const apps = resolveSeedApps(seedApps);
  const chrome = apps.find((app) => app.displayName === "Chrome") ?? apps[1]!;
  const messages = apps.find((app) => app.displayName === "Messages") ?? apps[7]!;
  const screen0 = apps.find((app) => app.displayName === "Screen0") ?? apps[0]!;

  return [
    {
      _id: `${MARKETING_DEMO_RULE_ID_PREFIX}target-chrome`,
      anonymousDeviceId: MARKETING_DEMO_DEVICE_ID,
      name: "Chrome",
      apps: [{
        key: targetAppKey(chrome),
        label: chrome.displayName,
        appIdentifier: chrome.appIdentifier,
        category: chrome.category,
      }],
      domains: [],
      createdAt: DEMO_NOW,
      updatedAt: DEMO_NOW,
    },
    {
      _id: `${MARKETING_DEMO_RULE_ID_PREFIX}target-social`,
      anonymousDeviceId: MARKETING_DEMO_DEVICE_ID,
      name: "Evening scroll",
      apps: [
        {
          key: targetAppKey(messages),
          label: messages.displayName,
          appIdentifier: messages.appIdentifier,
          category: messages.category,
        },
      ],
      domains: ["instagram.com", "reddit.com"],
      domainCategories: ["social"],
      createdAt: DEMO_NOW + 1,
      updatedAt: DEMO_NOW + 1,
    },
    {
      _id: `${MARKETING_DEMO_RULE_ID_PREFIX}target-focus`,
      anonymousDeviceId: MARKETING_DEMO_DEVICE_ID,
      name: "Focus apps",
      apps: [{
        key: targetAppKey(screen0),
        label: screen0.displayName,
        appIdentifier: screen0.appIdentifier,
        category: screen0.category,
      }],
      domains: [],
      createdAt: DEMO_NOW + 2,
      updatedAt: DEMO_NOW + 2,
    },
  ];
}

export function buildMarketingDemoRules(targets: Target[]): Rule[] {
  const chromeTarget = targets.find((target) => target._id.endsWith("target-chrome"))!;
  const socialTarget = targets.find((target) => target._id.endsWith("target-social"))!;
  const focusTarget = targets.find((target) => target._id.endsWith("target-focus"))!;

  return [
    {
      _id: `${MARKETING_DEMO_RULE_ID_PREFIX}rule-evening-chrome`,
      anonymousDeviceId: MARKETING_DEMO_DEVICE_ID,
      enabled: false,
      targetIds: [chromeTarget._id],
      days: [1, 2, 3, 4, 5],
      window: { startMinute: 19 * 60, durationMinutes: 120 },
      source: "smart_block",
      smartBlockGoal: "doom",
      createdAt: DEMO_NOW,
      updatedAt: DEMO_NOW,
    },
    {
      _id: `${MARKETING_DEMO_RULE_ID_PREFIX}rule-weekend-social`,
      anonymousDeviceId: MARKETING_DEMO_DEVICE_ID,
      enabled: false,
      targetIds: [socialTarget._id],
      days: [0, 6],
      window: { startMinute: 20 * 60 + 30, durationMinutes: 90 },
      createdAt: DEMO_NOW + 1,
      updatedAt: DEMO_NOW + 1,
    },
    {
      _id: `${MARKETING_DEMO_RULE_ID_PREFIX}rule-morning-focus`,
      anonymousDeviceId: MARKETING_DEMO_DEVICE_ID,
      enabled: false,
      targetIds: [focusTarget._id],
      days: [1, 2, 3, 4, 5],
      window: { startMinute: 8 * 60, durationMinutes: 60 },
      source: "smart_block",
      smartBlockGoal: "compulsivity",
      createdAt: DEMO_NOW + 2,
      updatedAt: DEMO_NOW + 2,
    },
  ];
}

export function isMarketingDemoRuleId(id: string): boolean {
  return id.startsWith(MARKETING_DEMO_RULE_ID_PREFIX);
}

export function mergeMarketingDemoRules(
  realRules: Rule[] | undefined,
  demoActive: boolean,
  seedApps: MarketingSeedApp[] = DEFAULT_MARKETING_SEED_APPS,
): Rule[] | undefined {
  if (!demoActive) return realRules;
  const targets = buildMarketingDemoTargets(seedApps);
  const demoRules = buildMarketingDemoRules(targets);
  if (realRules === undefined) return demoRules;
  if (realRules.length >= 2) return realRules;
  return [...demoRules, ...realRules];
}

export function mergeMarketingDemoTargets(
  realTargets: Target[] | undefined,
  demoActive: boolean,
  seedApps: MarketingSeedApp[] = DEFAULT_MARKETING_SEED_APPS,
): Target[] | undefined {
  if (!demoActive) return realTargets;
  const demoTargets = buildMarketingDemoTargets(seedApps);
  if (realTargets === undefined) return demoTargets;
  if (realTargets.length >= 2) return realTargets;
  return [...demoTargets, ...realTargets];
}
